import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { usePnlHistory } from '@/hooks/queries/useAnalytics';
import { formatCurrency, formatShortDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { PERIODS, type Period } from '@/components/analytics/periods';
import { periodPnl } from '@/lib/pnlPeriod';

const PERIOD_PHRASES: Record<Period, string> = {
  '7d': 'last 7 days',
  '30d': 'last 30 days',
  '90d': 'last 90 days',
  '1y': 'last year',
  all: 'all time',
};

export interface PnlChartProps {
  period: Period;
  onPeriodChange: (period: Period) => void;
}

/** Props-driven: the dashboard owns the period so it lives in the page's URL. */
export function PnlChart({ period, onPeriodChange }: PnlChartProps) {
  const { data: dataPoints, isLoading } = usePnlHistory(period);
  const navigate = useNavigate();

  // The line and the header are both the PERIOD's P&L: it starts at zero and ends on the total.
  // The all-time figure lives in the summary row above and in the tooltip.
  const { points: chartData, total } = periodPnl(dataPoints || []);

  return (
    <Card data-testid="pnl-chart">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>P&L Over Time</CardTitle>
          {total !== null && (
            <p className="text-sm text-gray-500 mt-1">
              <span
                data-testid="pnl-period-total"
                className={cn('font-semibold', total >= 0 ? 'text-green-600' : 'text-red-600')}
              >
                {formatCurrency(total, { showSign: true })}
              </span>{' '}
              <span data-testid="pnl-period-label">realized, {PERIOD_PHRASES[period]}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => onPeriodChange(p.value)}
                aria-pressed={period === p.value}
                className={cn(
                  'px-2 py-1 text-xs font-medium rounded transition-colors',
                  period === p.value
                    ? 'bg-brand-100 text-brand-700'
                    : 'text-gray-500 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner />
          </div>
        ) : !chartData || chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center">
            <EmptyState
              icon={<NoDataIcon />}
              title="No data available"
              description="Close some trades to see your P&L history"
            />
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                onClick={(state) => {
                  if (state?.activePayload?.[0]?.payload?.date) {
                    navigate(`/transactions?from=${state.activePayload[0].payload.date}&to=${state.activePayload[0].payload.date}`);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => formatShortDate(date)}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value).replace('.00', '')}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  width={60}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
                        <p className="text-sm text-gray-500">{formatShortDate(label)}</p>
                        <p className="text-sm font-medium">
                          Daily:{' '}
                          <span className={data.realizedPnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(data.realizedPnl)}
                          </span>
                        </p>
                        <p className="text-sm font-medium">
                          This period:{' '}
                          <span className={data.periodPnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(data.periodPnl)}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          All time: {formatCurrency(data.cumulativePnl)}
                        </p>
                        {data.breakdown && data.breakdown.length > 0 && (
                          <div className="border-t border-gray-100 pt-1 mt-1 space-y-0.5">
                            {data.breakdown.map((b: { symbol: string; pnl: number }, i: number) => (
                              <p key={i} className="text-xs">
                                <span className="text-gray-500">{b.symbol}</span>{' '}
                                <span className={b.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {formatCurrency(b.pnl)}
                                </span>
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Click to view transactions</p>
                      </div>
                    );
                  }}
                />
                <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="periodPnl"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: '#0ea5e9', stroke: '#fff', strokeWidth: 2 }}
                  name="This period"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
